import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { ChevronDown, X, AlertTriangle, ShieldCheck } from 'lucide-react';

// Roles a system_admin can preview the app as. Order = sidebar prominence.
const PREVIEW_ROLES = [
  { id: 'holding_admin', label: 'Holding Admin',  blurb: 'Tenant super-approver' },
  { id: 'chairman',      label: 'Chairman',       blurb: 'Approves Chairman step' },
  { id: 'ceo',           label: 'CEO',            blurb: 'Approves CEO step' },
  { id: 'cfo',           label: 'CFO',            blurb: 'Approves CFO step' },
  { id: 'legal',         label: 'Legal Counsel',  blurb: 'Approves Legal step' },
  { id: 'investor',      label: 'Investor',       blurb: 'Vetted-startup viewer' },
  { id: 'founder',       label: 'Founder',        blurb: 'Gated by founder journey' },
  { id: 'startup_member',label: 'Startup Member', blurb: 'Read-only founder peer' },
  { id: 'guest',         label: 'Guest',          blurb: 'Pre-vetting user' },
];

/**
 * Role impersonation dropdown trigger.
 *
 * v167: the trigger button IS the MakerStatusPill — clicking the
 * gold-on-black "MAKER · UNRESTRICTED" pill on the left of the header
 * opens the same role-preview menu that used to live in a separate
 * "View as: …" button on the right. One control, half the header
 * footprint, and the privilege state + role-preview action are now
 * co-located which matches user mental model ("I am MAKER. I want to
 * preview as X.").
 *
 * Still gated to true system_admins. Sends `X-Impersonate-Role` on
 * every subsequent request once a preview role is chosen. Audit-logged.
 */
export default function RoleImpersonator({ compact = false } = {}) {
  const { realRole, effectiveRole, isImpersonating, setImpersonatedRole } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (realRole !== 'system_admin') return null;

  const cloaked = !!isImpersonating;
  const currentLabel = cloaked
    ? (PREVIEW_ROLES.find(r => r.id === effectiveRole)?.label || effectiveRole)
    : 'UNRESTRICTED';

  // Compact = sidebar-rail variant (single letter); default = full pill
  // with the role label baked in (e.g. "MAKER · UNRESTRICTED" or
  // "MAKER · CLOAKED · CHAIRMAN").
  const padding = compact ? 'px-2 py-0.5 text-[9px]' : 'px-2.5 py-1 text-[10px]';
  const tone = cloaked
    ? 'bg-zinc-800/80 border-zinc-600 text-zinc-300 hover:border-zinc-400'
    : 'bg-black border-[#D4AF37] text-[#D4AF37] shadow-[0_0_10px_-2px_rgba(212,175,55,0.45)] hover:shadow-[0_0_14px_-2px_rgba(212,175,55,0.65)]';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="role-impersonator-trigger"
        onClick={() => setOpen(o => !o)}
        title={cloaked
          ? 'Maker (you) — currently previewing as another role. Click to switch or exit.'
          : 'Maker (you) — full unrestricted control. Click to preview the app as a different role.'}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 select-none font-bold uppercase tracking-[0.18em]
                    border rounded-full transition-all duration-200 cursor-pointer
                    ${padding} ${tone}`}
      >
        <ShieldCheck size={compact ? 9 : 11} strokeWidth={2.4} />
        {compact ? 'M' : (
          <>
            MAKER
            <span className="opacity-60 mx-0.5">·</span>
            <span className="truncate max-w-[110px]">
              {cloaked ? <>CLOAKED · {currentLabel.toUpperCase()}</> : 'UNRESTRICTED'}
            </span>
            <ChevronDown size={11} className="opacity-70 shrink-0 -mr-0.5" />
          </>
        )}
      </button>

      {open && !compact && (
        <div
          data-testid="role-impersonator-menu"
          role="menu"
          className="absolute left-0 mt-2 w-[300px] rounded-lg border border-slate-200 dark:border-white/15 bg-white dark:bg-[#0d0d0d] shadow-2xl z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-white/60 font-bold">Preview as role</div>
            <div className="text-xs text-slate-600 dark:text-white/70 mt-1">See exactly what each role sees. Audit-logged.</div>
          </div>

          {/* "Exit" / back-to-self row */}
          <button
            type="button"
            data-testid="role-impersonator-exit"
            onClick={() => { setImpersonatedRole(null); setOpen(false); }}
            className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 transition
              ${!isImpersonating
                ? 'bg-[#FFF8E5] dark:bg-[#3a2f0a]'
                : 'hover:bg-slate-50 dark:hover:bg-white/5'}`}
          >
            <div>
              <div className="text-sm font-bold text-slate-900 dark:text-white">System Admin (you)</div>
              <div className="text-[11px] text-slate-500 dark:text-white/60">Full platform owner view</div>
            </div>
            {!isImpersonating && <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#5E1F8B]">Active</span>}
          </button>

          <div className="max-h-[360px] overflow-y-auto py-1">
            {PREVIEW_ROLES.map((r) => {
              const active = isImpersonating && effectiveRole === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`role-impersonator-option-${r.id}`}
                  onClick={() => { setImpersonatedRole(r.id); setOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 transition
                    ${active
                      ? 'bg-[#FFF8E5] dark:bg-[#3a2f0a]'
                      : 'hover:bg-slate-50 dark:hover:bg-white/5'}`}
                >
                  <div>
                    <div className="text-sm font-medium text-slate-900 dark:text-white">{r.label}</div>
                    <div className="text-[11px] text-slate-500 dark:text-white/60">{r.blurb}</div>
                  </div>
                  {active && <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#5E1F8B]">Active</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Sticky warning banner that appears at the top of every dashboard page
 * whenever a system_admin (Maker) is previewing the app as a different
 * role. Amber/warning palette so it can't be mistaken for chrome.
 *
 * Copy is verbatim from the v155 design discussion:
 *   "⚠ YOU ARE CLOAKED AS [role] · CLICK TO RETURN TO MAKER VIEW"
 *
 * One-click exit; click anywhere on the bar OR the explicit Exit button
 * to drop back to true Maker view.
 */
export function ImpersonationBanner() {
  const { isImpersonating, effectiveRole, setImpersonatedRole, user } = useAuth();
  if (!isImpersonating) return null;
  const label = (PREVIEW_ROLES.find(r => r.id === effectiveRole)?.label || effectiveRole || 'unknown')
    .toString().toUpperCase();
  const tenant = user?.tenant_id || user?.tenant_slug || null;
  const exit = () => setImpersonatedRole(null);
  return (
    <button
      type="button"
      data-testid="impersonation-banner"
      onClick={exit}
      // Amber/yellow warning palette — semantically a "you-might-be-confused"
      // alert, NOT a brand chrome bar. Sticky so it survives scroll.
      className="sticky top-0 z-[60] w-full bg-amber-400 hover:bg-amber-300 text-black
                 text-[11px] font-bold uppercase tracking-[0.18em] px-4 py-2
                 flex items-center justify-center gap-3 cursor-pointer
                 border-b-2 border-amber-600 transition-colors group"
      aria-label="Exit cloak and return to Maker view"
    >
      <AlertTriangle size={14} className="text-amber-900 group-hover:rotate-[-8deg] transition-transform" />
      <span>
        You are <span className="underline">cloaked as {label}</span>
        {tenant ? <> · tenant <span className="underline">{tenant}</span></> : null}
        {' · '}
        <span className="opacity-80">click anywhere to return to Maker view</span>
      </span>
      <span
        data-testid="impersonation-banner-exit"
        className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-black text-amber-300 group-hover:bg-amber-900"
      >
        <X size={11}/> Exit
      </span>
    </button>
  );
}

/**
 * MakerStatusPill — back-compat alias.
 *
 * v167: the standalone Maker chip + the standalone RoleImpersonator
 * "View as:" button were merged into a single combined trigger (the
 * default export above). MakerStatusPill is kept as a thin alias so
 * existing imports keep working: in `compact` mode (sidebar rail) it
 * still renders a non-interactive monogram-style pill; in full mode it
 * now renders the merged trigger pill that opens the role-preview
 * dropdown when clicked.
 */
export function MakerStatusPill({ compact = false }) {
  const { realRole, isImpersonating } = useAuth();
  if (realRole !== 'system_admin') return null;
  // Compact = sidebar-rail variant — keep as a non-interactive monogram
  // so it doesn't fight the nav rail's icon column for clicks.
  if (compact) {
    const cloaked = !!isImpersonating;
    return (
      <div
        data-testid="maker-status-pill"
        data-cloaked={cloaked ? '1' : '0'}
        title={cloaked ? 'Maker · cloaked' : 'Maker · unrestricted'}
        className={`inline-flex items-center gap-1.5 select-none font-bold uppercase tracking-[0.18em]
                    border rounded-full px-2 py-0.5 text-[9px]
                    ${cloaked
                      ? 'bg-zinc-800/80 border-zinc-600 text-zinc-400'
                      : 'bg-black border-[#D4AF37] text-[#D4AF37] shadow-[0_0_10px_-2px_rgba(212,175,55,0.45)]'}`}
      >
        <ShieldCheck size={9} strokeWidth={2.4} />M
      </div>
    );
  }
  // Full mode = the merged dropdown trigger (with `data-testid="maker-status-pill"`
  // injected via the wrapper so older tests keep working).
  return (
    <span data-testid="maker-status-pill" data-cloaked={isImpersonating ? '1' : '0'}>
      <RoleImpersonator />
    </span>
  );
}
